import { useQuery } from "@tanstack/react-query";
import Color from "color";
import { useRouter } from "next/router";
import { Alert, Spinner } from "react-bootstrap";
import {
  getColorsForDataset,
  LinePlot,
  useAATheme,
} from "@bagel-web/components";
import { sessionQueries } from "../../queries";
import { CommonAPI, SessionXAxis } from "../../types";
import { formatNumber } from "../../util";

const SessionTokenUsageChart = ({
  sessionId,
  commonAPI,
  xAxis,
}: {
  sessionId: string;
  commonAPI: CommonAPI;
  xAxis: SessionXAxis;
}) => {
  const {
    data: tokenUsage,
    isPending,
    isError,
  } = useQuery({
    queryKey: sessionQueries.getTokenUsage(sessionId),
    queryFn: () => commonAPI.fetchSessionTokenUsage(sessionId),
  });
  const { isDarkMode } = useAATheme();
  const router = useRouter();

  if (isPending) {
    return <Spinner />;
  }
  if (isError) {
    return <Alert variant="danger">Error loading token usage</Alert>;
  }
  const points = tokenUsage?.points ?? [];
  if (points.length === 0) {
    // Old sessions with no points may simply never have called a model, so
    // this says the data may be gone rather than that it was.
    return tokenUsage?.retention_elapsed ? (
      <Alert variant="info" className="mb-0">
        Token usage breakdown may be unavailable for Sessions older than 90
        days.
      </Alert>
    ) : (
      <p className="text-muted mb-0">No token usage recorded.</p>
    );
  }

  const series = (
    key: "input_tokens" | "cached_input_tokens" | "output_tokens"
  ) =>
    points.map((point) => ({
      x: xAxis === "moment" ? point.moment : point.time * 1000,
      y: point[key],
    }));
  const input = getColorsForDataset(0, isDarkMode);
  const output = getColorsForDataset(1, isDarkMode);
  const datasets = [
    {
      label: "Input Tokens",
      borderColor: input.borderColor,
      backgroundColor: input.backgroundColor,
      data: series("input_tokens"),
    },
    // Cached tokens are a share of input tokens, so they shade the area under
    // the input line.
    {
      label: "Cached Input Tokens",
      borderColor: input.borderColor,
      backgroundColor: Color(input.borderColor).alpha(0.3).hexa(),
      borderWidth: 1,
      borderDash: [4, 4],
      fill: "origin" as const,
      data: series("cached_input_tokens"),
    },
    {
      label: "Output Tokens",
      borderColor: output.borderColor,
      backgroundColor: output.backgroundColor,
      data: series("output_tokens"),
    },
  ];

  let options = LinePlot.getDefaultOptions({ isDarkMode });
  const axisColor = options.scales?.y?.ticks?.color;
  const axisTitle = (text: string) => ({
    display: true,
    text,
    color: typeof axisColor === "string" ? axisColor : undefined,
  });
  options = {
    ...options,
    // Clicks pick the moment the tooltip shows: the one nearest the cursor
    // along the x-axis.
    interaction: { mode: "index", intersect: false },
    onClick: (_event, [item]) => {
      const point = item && points[item.index];
      if (point) {
        router.push(
          `/sessions/${sessionId}/agent/${point.agent_id}?momentId=${point.moment}`
        );
      }
    },
    elements: {
      point: { radius: 0, hoverRadius: 3 },
      line: { borderWidth: 2 },
    },
    scales: {
      ...options.scales,
      x:
        xAxis === "moment"
          ? {
              type: "linear",
              title: axisTitle("Moment"),
              bounds: "data",
              grid: { display: false },
              ticks: {
                maxTicksLimit: 6,
                precision: 0,
                color: axisColor,
              },
            }
          : {
              type: "time",
              title: axisTitle("Time"),
              time: { tooltipFormat: "MMM DD, YYYY HH:mm:ss" },
              bounds: "data",
              grid: { display: false },
              ticks: {
                maxTicksLimit: 6,
                color: axisColor,
              },
            },
      y: {
        ...options.scales?.y,
        title: axisTitle("Tokens"),
        ticks: {
          ...options.scales?.y?.ticks,
          callback: (value) => formatNumber(Number(value)),
        },
      },
    },
    plugins: {
      ...options.plugins,
      tooltip: {
        ...options.plugins?.tooltip,
        callbacks: {
          ...(xAxis === "moment" && {
            title: ([item]) => `Moment ${points[item?.dataIndex ?? 0]?.moment}`,
          }),
          label: (item) =>
            `${item.dataset.label}: ${formatNumber((item.raw as { y: number }).y)}`,
        },
      },
    },
  };

  return <LinePlot height="300px" data={{ datasets }} options={options} />;
};

export default SessionTokenUsageChart;
